import { Popover, Transition } from '@headlessui/react';
import { PlusCircleIcon } from '@heroicons/react/outline';
import { ButtonCTASmall } from 'components/ui';
import { compact, xorBy } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from 'utils/useQuery';
import { Metric } from './types';
import { useStoreMetrics } from './useStoreMetrics';

export const StoreMetricsAddMetric = () => {
  const {
    metrics,
    selectedMetrics,
    setSelectedMetrics,
    handleSaveMetricsToLocalStorage,
  } = useStoreMetrics();
  const [isOpen, setIsOpen] = useState(false);

  const allMetrics = metrics.map((metric) => metric);

  const [checkboxes, setCheckBoxes] = useState<
    { id: string; checked: boolean }[]
  >(() => {
    return metrics.map((metric) => {
      return {
        id: metric.id,
        checked: Boolean(
          selectedMetrics.find(
            (selectedMetric) => selectedMetric.id === metric.id
          )
        ),
      };
    });
  });
  const [search, setSearch] = useState('');

  let metricsToDisplay = allMetrics;

  if (search) {
    metricsToDisplay = allMetrics.filter((metric) =>
      metric.label.toLowerCase().includes(search.toLowerCase())
    );
  }

  const selectedCheckboxes = checkboxes.filter((item) => item.checked);

  const MAX_SELECTED_METRICS = 6;

  function handleCheckboxChange(id: string) {
    const checkboxIndex = checkboxes.findIndex((item) => item.id === id);

    const newCheckboxes = [...checkboxes];

    if (selectedCheckboxes.length < MAX_SELECTED_METRICS) {
      newCheckboxes[checkboxIndex].checked =
        !newCheckboxes[checkboxIndex].checked;
    } else {
      newCheckboxes[checkboxIndex].checked = false;
    }

    setCheckBoxes(newCheckboxes);
  }

  function handleDeselectAll() {
    const newCheckboxes = checkboxes.map((item) => {
      return {
        id: item.id,
        checked: false,
      };
    });

    setCheckBoxes(newCheckboxes);
  }

  function cancelSelectingMetrics() {
    setCheckBoxes(
      checkboxes.map((checkbox) => {
        const selectedMetric = selectedMetrics.find(
          (selectedMetric) => selectedMetric.id === checkbox.id
        );
        if (selectedMetric) {
          return { ...checkbox, checked: true };
        }

        return { ...checkbox, checked: false };
      })
    );

    setIsOpen(false);
  }

  const query = useQuery();
  const history = useHistory();

  function handleSubmit() {
    const checkedMetricsCompleteInfo = compact(
      selectedCheckboxes.map((metric) => {
        const selectedMetric = metrics.find(
          (selectedCheckbox) => selectedCheckbox.id === metric.id
        );

        if (selectedMetric) {
          return selectedMetric;
        }

        return null;
      })
    );

    const newSelectedMetrics: Metric[] = [];

    selectedMetrics.forEach((selectedMetric, index) => {
      const checkedMetric = checkedMetricsCompleteInfo.find(
        (checkedMetric) => checkedMetric.id === selectedMetric.id
      );

      if (checkedMetric) {
        newSelectedMetrics.push(selectedMetrics[index]);
      }
    });

    const arrayDifferenceToBeAdded = xorBy(
      newSelectedMetrics,
      checkedMetricsCompleteInfo,
      'id'
    );

    if (arrayDifferenceToBeAdded.length > 0) {
      newSelectedMetrics.push(...arrayDifferenceToBeAdded);
    }

    setSelectedMetrics(newSelectedMetrics);

    query.set('metrics', newSelectedMetrics.map((item) => item.id).join(','));
    history.push({
      search: query.toString(),
    });

    handleSaveMetricsToLocalStorage(newSelectedMetrics);
    setIsOpen(false);
  }

  const isSubmitButtonDisabled =
    selectedCheckboxes.length === 0 ||
    selectedCheckboxes.length > MAX_SELECTED_METRICS;

  useEffect(() => {
    setCheckBoxes(
      checkboxes.map((checkbox) => {
        const selectedMetric = selectedMetrics.find(
          (selectedMetric) => selectedMetric.id === checkbox.id
        );
        if (selectedMetric) {
          return { ...checkbox, checked: true };
        }

        return { ...checkbox, checked: false };
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetrics]);

  return (
    <Popover className="relative">
      <button
        className="flex items-center w-full px-4 py-2 space-x-1 font-bold text-white rounded-lg bg-segments-accent-color-1"
        onClick={() => setIsOpen(!isOpen)}
      >
        <PlusCircleIcon className="w-5 h-5 text-white" aria-hidden="true" />
        <p className="text-segments-13px">Select metric</p>
      </button>
      <div
        className={`${
          isOpen ? 'opacity-30 fixed inset-0' : 'opacity-0'
        } bg-black`}
        onClick={cancelSelectingMetrics}
      />
      <Transition
        as={Fragment}
        show={isOpen}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div>
          {isOpen && (
            <Popover.Panel
              static
              className="absolute right-0 z-10 flex flex-col justify-between w-screen max-w-xl mt-2 bg-white rounded-lg shadow-2xl ring-1 ring-black ring-opacity-10 h-96"
            >
              <div className="grid h-full grid-cols-3 overflow-y-hidden">
                <div className="h-full border-r pt-7">
                  <p className="px-8 font-bold text-black uppercase text-opacity-30 text-segments-13px">
                    Category
                  </p>
                  <div className="flex items-center justify-between py-3 pl-8 pr-4 mt-1 cursor-pointer hover:bg-segments-accent-color-2 hover:bg-opacity-10 bg-segments-accent-color-2 bg-opacity-10">
                    <div className="flex items-center space-x-3 pointer-events-none">
                      <p className="">Dashboard </p>
                      <span className="px-2 font-bold text-white text-segments-13px rounded-segments-32px bg-segments-accent-color-1">
                        {selectedCheckboxes.length}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 overflow-y-hidden ">
                  <div className="flex items-center px-2 py-1 pt-4 space-x-12 bg-white divide-x text-segments-13px">
                    <input
                      type="text"
                      placeholder="search filter"
                      className="w-full border border-gray-200 rounded-lg text-segments-13px"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="flex px-4 space-x-4 divide-x text-segments-link">
                      {/* <button
                        className="truncate focus:outline-none"
                        onClick={handleSelectAll}
                      >
                        select all
                      </button> */}
                      <button
                        className="pl-4 truncate focus:outline-none"
                        onClick={handleDeselectAll}
                      >
                        clear all
                      </button>
                    </div>
                  </div>

                  <div className="px-3 mt-2 overflow-y-auto h-3/4">
                    {metricsToDisplay.map((metric) => (
                      <label
                        className="items-center py-1.5 cursor-pointer flex truncate text-base"
                        htmlFor={metric.id}
                        key={metric.id}
                      >
                        <input
                          type="checkbox"
                          name={metric.id}
                          id={metric.id}
                          className="w-4 h-4 mr-2 rounded-sm focus:ring-0 text-segments-accent-color-1"
                          checked={
                            checkboxes.find(
                              (checkbox) => checkbox.id === metric.id
                            )?.checked
                          }
                          onChange={() => handleCheckboxChange(metric.id)}
                        />
                        {metric.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer Starts */}
              <div className="px-8 py-4 border-t ">
                <div className="flex items-center justify-between">
                  {/* <button
                    className="disabled:cursor-not-allowed text-segments-link focus:outline-none disabled:opacity-50"
                    onClick={handleDeselectAll}
                  >
                    clear all
                  </button> */}
                  <div className="flex items-center justify-end ">
                    <p
                      className={`${
                        selectedCheckboxes.length >= MAX_SELECTED_METRICS
                          ? 'text-segments-danger'
                          : 'text-gray-400'
                      }`}
                    >
                      {selectedCheckboxes.length} of 6 selected
                    </p>
                  </div>
                  <div className="flex space-x-4">
                    <ButtonCTASmall
                      variant="secondary"
                      type="button"
                      onClick={cancelSelectingMetrics}
                    >
                      <p className="px-4 font-normal text-segments-13px">
                        Cancel
                      </p>
                    </ButtonCTASmall>
                    <ButtonCTASmall
                      variant="main"
                      type="button"
                      disabled={isSubmitButtonDisabled}
                      onClick={handleSubmit}
                    >
                      <p className="px-4 font-bold text-segments-13px">Apply</p>
                    </ButtonCTASmall>
                  </div>
                </div>
              </div>

              {/* Footer Ends */}
            </Popover.Panel>
          )}
        </div>
      </Transition>
    </Popover>
  );
};
